"use client";
/**
 * No media at all: the prescription's figures and the record's own labelled
 * facts, column-aligned.
 *
 * This is the composition a generated plan's slot is actually made of. A slot
 * publishes three sets of `8-12` with 75 seconds between them and no
 * instructions, no category and no video flags — so a card that leads with a
 * picture or with steps would lead with the two fields the slot does not have.
 *
 * Where the record carries no prescription — every catalog row — the figure
 * slot is absent rather than zeroed, and the fact group carries the card on its
 * own. A `0 × —` is a claim the capture does not make.
 */
import type { ReactElement } from "react";
import {
  CresultcardFactGroup,
  CresultcardIdentity,
  CresultcardStack,
  CRC_TILE_INSET,
  CresultcardDensity,
  CresultcardFactGroupShape,
  CresultcardFactWeight,
  CresultcardFigureScale,
  CresultcardIdentitySlot,
  CresultcardStackDensity,
  CresultcardTilePadding,
  CresultcardTitleLines,
  type CresultcardFactEntry,
} from "@/components/ui/Cresultcard";
import {
  C_WORKOUT_CARD_MODIFIER,
} from "./cworkoutcard.constants";
import { cworkoutcardVendorWords } from "./cworkoutcard.model";
import {
  CworkoutcardMuscleLine,
  CworkoutcardPrescription,
} from "./CworkoutcardMeta";
import { CworkoutcardShell } from "./CworkoutcardShell";
import { CworkoutcardStateCapsule } from "./CworkoutcardStateCapsule";
import {
  CworkoutcardVariant,
  type CworkoutcardData,
  type CworkoutcardVariantProps,
} from "./cworkoutcard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
const CARD_TRUNCATION: readonly CresultcardIdentitySlot[] = [
  CresultcardIdentitySlot.Title,
];

/**
 * Every labelled cell the board draws, and only the ones this record filled: a
 * plan slot has no category and a catalog row has no order, and an empty cell
 * with a label above it reads as a value that failed to arrive.
 */
function boardFacts(data: CworkoutcardData): readonly CresultcardFactEntry[] {
  return [
    {
      label: "Muscle",
      value: cworkoutcardVendorWords(data.muscleGroup),
      weight: CresultcardFactWeight.Short,
    },
    {
      label: "Equipment",
      value: cworkoutcardVendorWords(data.equipment),
      weight: CresultcardFactWeight.Short,
    },
    {
      label: "Difficulty",
      value: cworkoutcardVendorWords(data.difficulty),
      weight: CresultcardFactWeight.Short,
    },
    ...(data.category == null
      ? []
      : [
          {
            label: "Category",
            value: cworkoutcardVendorWords(data.category),
            weight: CresultcardFactWeight.Short,
          },
        ]),
    ...(data.prescription == null
      ? []
      : [
          {
            label: "Order",
            value: String(data.prescription.order),
            weight: CresultcardFactWeight.Short,
          },
        ]),
  ];
}

export function CworkoutcardLoadBoard({
  data,
  openControl,
}: CworkoutcardVariantProps): ReactElement {
  return (
    <CworkoutcardShell
      density={CresultcardDensity.Card}
      head={
        <div
          style={{ minWidth: 0, padding: CARD_INSET }}
        >
          <CresultcardIdentity
            openControl={openControl}
            secondary={
              <CworkoutcardMuscleLine
                data={data}
              />
            }
            title={data.title}
            titleLines={CresultcardTitleLines.Two}
            trailing={
              data.prescription == null ? (
                <CworkoutcardStateCapsule
                  data={data}
                />
              ) : (
                <CworkoutcardPrescription
                  data={data}
                  scale={CresultcardFigureScale.Card}
                />
              )
            }
            truncationOrder={CARD_TRUNCATION}
          />
        </div>
      }
      modifier={C_WORKOUT_CARD_MODIFIER[CworkoutcardVariant.LoadBoard]}
      openable
      variant={CworkoutcardVariant.LoadBoard}
      body={
        <div
          style={{
            minWidth: 0,
            paddingInline: CARD_INSET,
            paddingBlockEnd: CARD_INSET,
          }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Regular}
          >
            <CresultcardFactGroup
              entries={boardFacts(data)}
              group={CresultcardFactGroupShape.Column}
            />
          </CresultcardStack>
        </div>
      }
    />
  );
}
